import { Checkbox } from '../../../../widgets/checkbox';
import { SearchInput } from '../../../../widgets/input';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styled from 'styled-components';
import EyeClosedIcon from './icons/eyeClosedIcon.svg';
import EyeIcon from './icons/eyeIcon.svg';
import FileIcon from './icons/fileIcon.svg';
import FolderCloseIcon from './icons/folderCloseIcon.svg';
import FolderOpenIcon from './icons/folderOpenIcon.svg';
import OverlayPanel from './overlayPanel';

const ICONS = {
    folderClosed: <FolderCloseIcon />,
    folderOpen: <FolderOpenIcon />,
    file: <FileIcon />,
    eye: <EyeIcon />,
    eyeOff: <EyeClosedIcon />
};

const DragPreviewLabel = styled.div<{ x: number; y: number }>`
    position: fixed;
    top: ${(props) => props.y}px;
    left: ${(props) => props.x}px;
    pointer-events: none;
    background: rgba(0, 0, 0, 0.75);
    color: #fff;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    z-index: 9999;
`;

const Arrow = styled.span<{ $isOpen: boolean }>`
    display: inline-block;
    width: 12px;
    text-align: center;
    cursor: pointer;
    user-select: none;
    transform: rotate(${(props) => (props.$isOpen ? 90 : 0)}deg);
    transition: transform 0.2s;
    margin-right: 4px;
`;

const TreeNodeContainer = styled.div<{
    $depth: number;
    $isDragging: boolean;
    $isDragover: boolean;
    $selected: boolean;
    $isVisible: boolean;
}>`
    padding-left: ${(props) => props.$depth * 20}px;
    cursor: pointer;
    display: flex;
    align-items: center;
    padding: 4px;
    background: ${(props) => (props.$isDragover ? '#e0e0e0' : props.$selected ? '#d0ebff' : 'transparent')};
    opacity: ${(props) => (props.$isDragging ? 0.5 : props.$isVisible ? 1 : 0.4)};
    &:hover {
        background: #f0f0f0;
    }
`;

const TreeNodeInnerContainer = styled.div`
    min-height: 100%;
    display: flex;
    flex-direction: column;
    & > :first-child {
        margin-top: 10px;
    }
`;

const SearchInputContainer = styled.div`
    padding: 10px 0px;
`;

const ChildrenContainer = styled.div<{ $depth: number }>`
    padding-left: ${(props) => props.$depth * 20}px;
`;

const Icon = styled.span`
    margin-right: 8px;
    user-select: none;
`;
const CustomIconContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    margin-right: 8px;
`;
const VisibilityIcon = styled.span`
    margin-right: 8px;
    cursor: pointer;
    user-select: none;
`;
const NodeName = styled.span`
    user-select: none;
    flex: 1;
`;

// Interfaccia per l'handle del TreeComponent
export interface TreeHandle {
    closeOverlay: () => void;
    selectAndOpenOverlay: (nodeId: string) => void;
    groupSelected?: (name: string, flag: boolean, icon: React.ReactNode, component: () => React.ReactNode) => void;
    removeSelected?: () => void;
}

export interface TreeComponentProps {
    treeState: {
        moveSelection: any;
        nodes: any[];
        openNodes: Set<string>;
        selectedIds: Set<string>;
        draggedId: string | null;
        dropTargetId: string | null;
        overlayNodeId: string | null;
        flatVisibleList: any[];
        setDraggedId: (id: string | null) => void;
        setDropTargetId: (id: string | null) => void;
        setOverlayNodeId: (id: string | null) => void;
        findNodeById: (id: string) => any;
        findParentId: (id: string) => string | null;
        findNodeByDataID: (nodes: any[], dataID: string) => any;
        toggleSelection: (id: string, shiftKey: boolean, ctrlKey: boolean) => void;
        toggleNode: (id: string) => void;
        toggleVisibility: (id: string) => void;
        setSelectedIds: (ids: Set<string>) => void;
        canTurnIntoFolder?: (node: any) => boolean;
    };
    filter?: string;
    setFilter?: (filter: string) => void;
    hideSearchInput?: boolean;
    hideSelectAll?: boolean;
    disableDragAndDrop?: boolean;
    onNodeMoved: (nodeId: string, targetParentId: string | null, index: number) => void;
    onToggleNodeVisibility?: (meshId: string, visibility: boolean) => void;
    showVisibilityIcon?: boolean; // New prop to control visibility icon display
}

const TreeComponent = forwardRef<TreeHandle, TreeComponentProps>(
    (
        { treeState, filter, setFilter, hideSearchInput, hideSelectAll, disableDragAndDrop, onNodeMoved, onToggleNodeVisibility, showVisibilityIcon = true },
        ref
    ) => {
        const {
            nodes,
            openNodes,
            selectedIds,
            draggedId,
            dropTargetId,
            overlayNodeId,
            flatVisibleList,
            setDraggedId,
            setDropTargetId,
            setOverlayNodeId,
            findNodeById,
            findParentId,
            findNodeByDataID,
            toggleSelection,
            toggleNode,
            toggleVisibility,
            setSelectedIds
        } = treeState;

        const treeContainerRef = useRef<HTMLDivElement>(null);

        const [dragPreview, setDragPreview] = useState<{
            x: number;
            y: number;
            label: string;
        } | null>(null);

        // Esponi i metodi tramite useImperativeHandle
        useImperativeHandle(
            ref,
            () => ({
                closeOverlay: () => {
                    setOverlayNodeId(null);
                },
                selectAndOpenOverlay: (dataID: string) => {
                    console.log('selectAndOpenOverlay called with meshID:', dataID);
                    console.log('Current openNodes:', Array.from(openNodes));

                    // 1) Usa la ricerca ricorsiva su tutti i rami dell'albero
                    const targetNode = findNodeByDataID(treeState.nodes, dataID);

                    if (!targetNode) {
                        console.warn(`Nessun nodo trovato per meshId ${dataID}`);
                        return;
                    }

                    // 2) Trova tutti i nodi padre che devono essere aperti
                    const nodesToOpen: string[] = [];
                    let currentId = targetNode.id;

                    while (currentId) {
                        const parentId = findParentId(currentId);
                        if (parentId) {
                            // Aggiungi solo se non è già aperto
                            if (!openNodes.has(parentId)) {
                                nodesToOpen.push(parentId);
                                console.log(`Parent ${parentId} needs to be opened`);
                            } else {
                                console.log(`Parent ${parentId} is already open, skipping`);
                            }
                            currentId = parentId;
                        } else {
                            break;
                        }
                    }

                    // 3) Apri solo i nodi padre che NON sono già aperti
                    nodesToOpen.forEach((nodeId) => {
                        console.log(`Opening node ${nodeId}`);
                        toggleNode(nodeId);
                    });

                    // 4) Selezione
                    setSelectedIds(new Set([targetNode.id]));

                    // 5) Apertura overlay
                    if (targetNode.component && !targetNode.doNotOpenOverlay) {
                        setOverlayNodeId(targetNode.id);
                    }
                },
                groupSelected: (name: string, flag: boolean, icon: React.ReactNode, component: () => React.ReactNode) => {
                    // Implementazione per raggruppamento se necessaria
                    console.log('Group selected:', name, flag, icon, component);
                },
                removeSelected: () => {
                    // Implementazione per rimozione se necessaria
                    console.log('Remove selected');
                }
            }),
            // aggiungo queste dipendenze perchè ad esempio, usando openNodes nella selectAndOpenOverlay, prendendo il valore alla creazione dell'imperative,
            // openNodes restava sempre vuoto, quindi ho aggiunto ognuno per:
            // openNodes - per controllare quali nodi sono aperti
            // findNodeByDataID - per trovare il nodo target
            // findParentId - per risalire la catena dei parent
            // toggleNode - per aprire i nodi
            // setSelectedIds - per selezionare il nodo
            // setOverlayNodeId - per aprire l'overlay
            // treeState.nodes - per la ricerca nel tree
            [setOverlayNodeId, openNodes, findNodeByDataID, findParentId, toggleNode, setSelectedIds, treeState.nodes]
        );

        const handleSelectAll = useCallback(
            (checked: boolean) => {
                setSelectedIds(new Set(checked ? flatVisibleList.map((n: any) => n.id) : []));
            },
            [flatVisibleList, setSelectedIds]
        );

        const isAllSelected = selectedIds.size > 0 && flatVisibleList.every((n: any) => selectedIds.has(n.id));

        // Gestori Drag and Drop corretti
        const resetDragState = useCallback(() => {
            setDraggedId(null);
            setDropTargetId(null);
            setDragPreview(null);
        }, [setDraggedId, setDropTargetId]);

        // 1) drag start
        const handleDragStart = useCallback(
            (e: React.DragEvent, id: string) => {
                e.stopPropagation();
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', id);

                // Se il nodo non è selezionato, selezionalo
                if (!selectedIds.has(id)) {
                    setSelectedIds(new Set([id]));
                }

                setDraggedId(id);

                // Usa il primo nodo selezionato per il preview
                const draggedNodes = selectedIds.has(id) ? Array.from(selectedIds) : [id];
                const first = draggedNodes[0];
                const n = findNodeById(first);

                if (n) {
                    const label = draggedNodes.length > 1 ? `${n.name} (+${draggedNodes.length - 1} others)` : n.name;
                    setDragPreview({
                        x: e.clientX + 8,
                        y: e.clientY + 8,
                        label
                    });
                }
            },
            [selectedIds, findNodeById, setSelectedIds]
        );

        // 2) drag over
        const handleDragOver = useCallback(
            (e: React.DragEvent, id: string) => {
                e.preventDefault();
                e.stopPropagation();

                // Non permettere il drop su un nodo che è tra quelli draggati
                const draggedNodes = draggedId && selectedIds.has(draggedId) ? Array.from(selectedIds) : draggedId ? [draggedId] : [];

                if (draggedNodes.includes(id)) {
                    e.dataTransfer.dropEffect = 'none';
                    return;
                }

                // Verifica che non stiamo droppando un nodo su un suo discendente
                if (draggedId && treeState.findParentId) {
                    let currentParent = treeState.findParentId(id);
                    while (currentParent) {
                        if (draggedNodes.includes(currentParent)) {
                            e.dataTransfer.dropEffect = 'none';
                            return;
                        }
                        currentParent = treeState.findParentId(currentParent);
                    }
                }

                e.dataTransfer.dropEffect = 'move';
                setDropTargetId(id);
            },
            [draggedId, selectedIds, treeState]
        );

        // 3) drop su un nodo
        const handleDrop = useCallback(
            (e: React.DragEvent, targetId: string) => {
                e.preventDefault();
                e.stopPropagation();

                if (!draggedId || !treeState.moveSelection) {
                    resetDragState();
                    return;
                }

                // Determina quali nodi spostare
                const nodesToMove = selectedIds.has(draggedId) ? Array.from(selectedIds) : [draggedId];

                // Non permettere il drop su se stesso o sui propri discendenti
                if (nodesToMove.includes(targetId)) {
                    resetDragState();
                    return;
                }

                // Verifica discendenti
                let isInvalidDrop = false;
                if (treeState.findParentId) {
                    let currentParent = treeState.findParentId(targetId);
                    while (currentParent && !isInvalidDrop) {
                        if (nodesToMove.includes(currentParent)) {
                            isInvalidDrop = true;
                        }
                        currentParent = treeState.findParentId(currentParent);
                    }
                }

                if (isInvalidDrop) {
                    resetDragState();
                    return;
                }

                // 🔧 NUOVO: Controlla se il nodo target può diventare cartella
                const targetNode = treeState.findNodeById(targetId);
                if (targetNode && treeState.canTurnIntoFolder && !treeState.canTurnIntoFolder(targetNode)) {
                    console.warn('Cannot drop on node that cannot turn into folder:', targetNode.name);
                    resetDragState();
                    return;
                }

                // Esegui effettivamente lo spostamento nel TreeNodeManager
                console.log('TreeComponent: Moving nodes:', nodesToMove, 'to parent:', targetId);
                treeState.moveSelection(nodesToMove, targetId);

                // Chiamiamo il callback onNodeMoved per notificare il componente parent
                const nodeToMove = nodesToMove[0];
                onNodeMoved(nodeToMove, targetId, 0);

                // Assicurati che il target sia espanso se ha ricevuto dei figli
                if (!openNodes.has(targetId)) {
                    toggleNode(targetId);
                }

                resetDragState();
            },
            [draggedId, selectedIds, treeState, openNodes, toggleNode, resetDragState, onNodeMoved]
        );

        // 4) dragEnd → fuori dal container
        const handleDragEnd = useCallback(
            (e: React.DragEvent) => {
                if (!draggedId || !treeState.moveSelection) {
                    resetDragState();
                    return;
                }

                const rect = treeContainerRef.current?.getBoundingClientRect();
                if (rect) {
                    const { clientX: x, clientY: y } = e;
                    const outside = x < rect.left || x > rect.right || y < rect.top || y > rect.bottom;

                    if (outside) {
                        // Determina quali nodi spostare
                        const nodesToMove = selectedIds.has(draggedId) ? Array.from(selectedIds) : [draggedId];

                        // Sposta tutti i nodi selezionati nella root
                        treeState.moveSelection(nodesToMove, null);
                    }
                }

                resetDragState();
            },
            [draggedId, selectedIds, treeState, resetDragState]
        );

        // 5) drag leave - per resettare il drop target quando usciamo da un nodo
        const handleDragLeave = useCallback(
            (e: React.DragEvent, id: string) => {
                e.preventDefault();
                e.stopPropagation();

                // Resetta il drop target solo se stiamo realmente uscendo dal nodo
                // e non entrando in un suo figlio
                const rect = e.currentTarget.getBoundingClientRect();
                const { clientX: x, clientY: y } = e;
                const outside = x < rect.left || x > rect.right || y < rect.top || y > rect.bottom;

                if (outside && dropTargetId === id) {
                    setDropTargetId(null);
                }
            },
            [dropTargetId]
        );

        // Effetto per aggiornare la posizione del drag preview
        useEffect(() => {
            const onDragOverWindow = (e: DragEvent) => {
                if (dragPreview) {
                    setDragPreview({
                        ...dragPreview,
                        x: e.clientX + 12,
                        y: e.clientY + 12
                    });
                }
            };

            window.addEventListener('dragover', onDragOverWindow);
            return () => window.removeEventListener('dragover', onDragOverWindow);
        }, [dragPreview]);

        // Cleanup quando il componente viene smontato
        useEffect(() => {
            return () => {
                resetDragState();
            };
        }, [resetDragState]);

        // Funzione di rendering ricorsivo
        const renderTree = useCallback(
            (node: any, depth = 0, parent: string | null = null) => {
                const isFolder = node.children?.length > 0;
                const isOpen = openNodes.has(node.id);
                const isVis = node.visible !== false;
                const isSel = selectedIds.has(node.id);

                return (
                    <div key={node.id}>
                        <TreeNodeContainer
                            ref={treeContainerRef}
                            draggable={!disableDragAndDrop}
                            onDragStart={(e) => handleDragStart(e, node.id)}
                            onDragOver={(e) => handleDragOver(e, node.id)}
                            onDragLeave={(e) => handleDragLeave(e, node.id)}
                            onDrop={(e) => handleDrop(e, node.id)}
                            onDragEnd={handleDragEnd}
                            $depth={depth}
                            $isDragging={draggedId === node.id}
                            $isDragover={dropTargetId === node.id}
                            $selected={isSel}
                            $isVisible={isVis}
                            onClick={(e) => {
                                console.log('Node clicked:', node.id);
                                node.onNodeClick?.();
                                e.stopPropagation();
                                toggleSelection(node.id, e.shiftKey, e.ctrlKey || e.metaKey);

                                if (node.doNotOpenOverlay) {
                                    setOverlayNodeId(null);
                                    return;
                                }

                                if (node.component) {
                                    setOverlayNodeId(node.id);
                                }
                            }}
                        >
                            {isFolder && (
                                <Arrow
                                    $isOpen={isOpen}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleNode(node.id);
                                    }}
                                >
                                    ▶
                                </Arrow>
                            )}

                            {node.customIcon ? (
                                <>
                                    {showVisibilityIcon && (
                                        <VisibilityIcon
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleVisibility(node.id);
                                                onToggleNodeVisibility?.(node.data.id, node.visible);
                                            }}
                                        >
                                            {isVis ? ICONS.eye : ICONS.eyeOff}
                                        </VisibilityIcon>
                                    )}
                                    <CustomIconContainer>{node.customIcon}</CustomIconContainer>
                                </>
                            ) : (
                                <>
                                    {showVisibilityIcon && (
                                        <VisibilityIcon
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleVisibility(node.id);
                                                onToggleNodeVisibility?.(node.data.id, node.visible);
                                            }}
                                        >
                                            {isVis ? ICONS.eye : ICONS.eyeOff}
                                        </VisibilityIcon>
                                    )}
                                    <Icon>{isFolder ? (isOpen ? ICONS.folderOpen : ICONS.folderClosed) : ICONS.file}</Icon>
                                </>
                            )}

                            <NodeName>{node.name}</NodeName>
                        </TreeNodeContainer>

                        {isFolder && isOpen && isVis && node.children && (
                            <ChildrenContainer $depth={depth + 1}>
                                {[...node.children]
                                    .sort((a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0))
                                    .map((child: any) => renderTree(child, depth + 1, node.id))}
                            </ChildrenContainer>
                        )}
                    </div>
                );
            },
            [
                openNodes,
                selectedIds,
                draggedId,
                dropTargetId,
                disableDragAndDrop,
                toggleSelection,
                toggleNode,
                toggleVisibility,
                handleDragStart,
                handleDragOver,
                handleDrop,
                handleDragEnd,
                setOverlayNodeId
            ]
        );

        return (
            <TreeNodeInnerContainer ref={treeContainerRef}>
                {!hideSelectAll && (
                    <Checkbox
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                        label='Select all items'
                        indeterminate={selectedIds.size > 0 && !isAllSelected}
                    />
                )}

                {!hideSearchInput && setFilter && (
                    <SearchInputContainer>
                        <SearchInput placeholder='Filters items...' value={filter || ''} onChange={(e) => setFilter!(e.currentTarget.value)} />
                    </SearchInputContainer>
                )}

                {nodes.sort((a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0)).map((node: any) => renderTree(node, 0, null))}

                {dragPreview && (
                    <DragPreviewLabel x={dragPreview.x} y={dragPreview.y}>
                        {dragPreview.label}
                    </DragPreviewLabel>
                )}

                {overlayNodeId && (
                    <OverlayPanel onClose={() => setOverlayNodeId(null)}>
                        {(() => {
                            const node = findNodeById(overlayNodeId);
                            if (!node?.component) return <div>No component selected.</div>;

                            return typeof node.component === 'function' ? node.component() : node.component;
                        })()}
                    </OverlayPanel>
                )}
            </TreeNodeInnerContainer>
        );
    }
);

export default TreeComponent;
